import React, { useCallback, useEffect, useState, useMemo } from 'react';
import DSSchoolSelector from '@ajat/design-system/SchoolSelector';
import useSchools from '../../../../hooks/useSchools';
import { useCart } from '../../../../context/CartContext';
import { saveSchoolSelection } from '../../../../services/schoolSelectionStore';
import {
  readFreshSelection,
  notifySchoolSelectionUpdated,
  SCHOOL_SELECTION_EVENT,
} from '../../../../services/schoolSelectionTtl';

/* CLS-FI school selector wrapper — same behaviour as CLS-DK (2026-10-14 alignment):
 * full selection persisted ({school{id,slug,name,classes,seasons}, season, class, savedAt}),
 * 24h TTL, prefill from a shop URL, cross-tab sync, cart scope push. No "book a meeting"
 * escape hatch on FI — the "school not found" link goes to the contact page instead. */

const STORAGE_KEY = 'clsfi_school_selection';

/**
 * Format a date string (YYYY-MM-DD) to Finnish display: "D.M.YYYY"
 */
function formatDateFI(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00'); // avoid timezone shift
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

function loadSelection() {
  // Honour the 24h TTL (clears expired/legacy entries).
  return readFreshSelection(STORAGE_KEY);
}

const saveSelection = (school, season, cls) => saveSchoolSelection(STORAGE_KEY, school, season, cls);

/**
 * Drop a lingering class-shop `?class=` param from the address bar once the
 * customer picks (or clears) a class themselves — otherwise the link's class
 * would re-apply over their choice on the next mount. Only the query string is
 * rewritten: on the public domains the path shown is a translated one
 * (config/translatingHistory), so it must be left exactly as-is.
 */
function stripClassParamFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('class')) return;
    url.searchParams.delete('class');
    const search = url.searchParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${search ? `?${search}` : ''}${url.hash}`,
    );
  } catch { /* non-browser context — ignore */ }
}

const SchoolSelector = ({ isOpen, onClose, onSelect, onNotFoundClick, prefill = null, ...props }) => {
  const { schools, loading } = useSchools('clsfi');

  // A school-shop URL that opened the popup already names the school and
  // (via ?shop=) the shop: resolve both from the live schools list so the
  // popup restores them as its initial values and opens on the class step.
  // Falls back to the stored selection when the slugs don't resolve.
  const prefilled = useMemo(() => {
    if (!prefill?.schoolSlug || loading) return null;
    const school = schools.find((s) => s.slug === prefill.schoolSlug);
    if (!school) return null;
    const season = prefill.shopSlug
      ? (school.seasons ?? []).find((se) => se.slug === prefill.shopSlug) ?? null
      : null;
    return { school, season };
  }, [prefill?.schoolSlug, prefill?.shopSlug, schools, loading]);
  const { setSchool: setCartSchool } = useCart('clsfi');

  const [saved, setSaved] = useState(loadSelection);

  // Keep `saved` (the popup's initialSchool/Season/Class) in sync with writes
  // made OUTSIDE this component — a class-shop link (?class=) or a direct shop
  // URL persists the selection from SchoolShop's URL effect, and without this
  // the reopened popup pre-selected the PREVIOUS class. The DS popup restores
  // from the initial* props on every open, so refreshing state here is enough.
  useEffect(() => {
    const refresh = () => setSaved(loadSelection());
    window.addEventListener(SCHOOL_SELECTION_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(SCHOOL_SELECTION_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const getSchoolInfo = useCallback((school) => {
    if (!school) return null;
    const data = schools.find((s) => s.id === school.id);
    if (!data?.shopOpensAt || !data?.shopClosesAt) return null;

    const now = new Date();
    const opens = new Date(data.shopOpensAt + 'T00:00:00');
    const closes = new Date(data.shopClosesAt + 'T23:59:59');
    const isOpen = now >= opens && now <= closes;

    return {
      statusLabel: isOpen
        ? 'KOULUSI VERKKOKAUPPA ON AUKI'
        : 'KOULUSI VERKKOKAUPPA ON SULJETTU',
      dateRange: `${formatDateFI(data.shopOpensAt)} - ${formatDateFI(data.shopClosesAt)}`,
      description: isOpen
        ? 'Muista tehdä tilauksesi tänä aikana saadaksesi alennuksen.'   // FI-TODO review
        : null,
    };
  }, [schools]);

  const handleSelect = useCallback((selection) => {
    saveSelection(selection.school, selection.season, selection.class);
    setSaved({ school: selection.school, season: selection.season, class: selection.class });
    stripClassParamFromUrl();
    // Push the selection to the BE cart so orders carry school, shop and
    // class. The BE keeps the cart only when all three match its current
    // values; any change rotates to a fresh cart.
    if (selection?.school?.id) {
      setCartSchool({
        school_id: selection.school.id,
        shop_id: selection.season?.id ? Number(selection.season.id) : null,
        school_class: selection.class ?? null,
      }).catch(() => { /* cart sync is best-effort */ });
    }
    onSelect?.(selection);
  }, [onSelect, setCartSchool]);

  const handleClear = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    notifySchoolSelectionUpdated(STORAGE_KEY);
    stripClassParamFromUrl();
    setSaved(null);
    onSelect?.(null);
  }, [onSelect]);

  return (
    <DSSchoolSelector
      isOpen={isOpen}
      onClose={onClose}
      onSelect={handleSelect}
      onNotFoundClick={onNotFoundClick}
      banner={require('../../../../assets/cls-fi/common/school-selector-banner.jpg')}
      // Only schools that currently have an open shop are selectable in the
      // popup (the API's `seasons` are the open+active shops).
      schools={loading ? [] : schools.filter((s) => (s.seasons ?? []).length > 0)}
      getSchoolInfo={getSchoolInfo}
      onClear={handleClear}
      clearLabel="Poista kouluvalinta"
      initialSchool={prefilled?.school ?? saved?.school ?? null}
      initialSeason={prefilled ? (prefilled.season ?? null) : (saved?.season ?? null)}
      initialClass={prefilled && saved?.school?.id !== prefilled.school.id ? null : (saved?.class ?? null)}
      requireClass={true}
      requireSeason={true}
      title="Valitse koulusi"
      infoText="Valitse koulusi saadaksesi käyttöösi ainutlaatuiset hinnat, tuotteet ja toimituksen kouluun."
      schoolPlaceholder="Kirjoita koulusi tähän"
      seasonPlaceholder="Valitse mallisto"   // FI-TODO review
      classPlaceholder="Valitse luokkasi"
      noClassesText="Tälle kaupalle ei ole vielä avoimia luokkia. Ota meihin yhteyttä, niin autamme sinua eteenpäin."   // FI-TODO review
      submitLabel="Jatka"
      notFoundTitle="Etkö löydä kouluasi?"
      // The DS default link is the Danish "OPRET KLASSESHOP" — FI has no create-school
      // page; the Header's onNotFoundClick navigates to the contact page.
      notFoundLinkLabel="OTA YHTEYTTÄ"
      notFoundLinkHref="/clothing/cls-fi/contact"
      {...props}
    />
  );
};

export default SchoolSelector;
